<template>
  <aside class="v2-event-filters" :class="{ 'v2-event-filters--open': mobileOpen }">
    <header class="v2-event-filters__head">
      <h3 class="v2-event-filters__title">
        <v-icon size="18" color="primary">mdi-filter-variant</v-icon>
        Filtros
      </h3>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="v2-event-filters__clear"
        @click="onClear"
      >
        Limpiar
      </button>
    </header>

    <div class="v2-event-filters__group">
      <BaseInput
        v-model="searchProxy"
        label="Buscar"
        placeholder="Buscar por nombre"
        @input="onSearchInput"
      />
    </div>

    <div class="v2-event-filters__group">
      <h4 class="v2-event-filters__group-title">Modalidad</h4>
      <div class="v2-event-filters__chips" role="group" aria-label="Filtrar por modalidad">
        <button
          v-for="opt in typeOptions"
          :key="opt.value"
          type="button"
          class="v2-event-filters__chip"
          :class="{ 'v2-event-filters__chip--active': typesProxy.includes(opt.value) }"
          :aria-pressed="typesProxy.includes(opt.value)"
          @click="toggleType(opt.value)"
        >
          <v-icon size="14">{{ opt.icon }}</v-icon>
          <span>{{ opt.label }}</span>
        </button>
      </div>
    </div>

    <div v-if="categoryOptions.length" class="v2-event-filters__group">
      <h4 class="v2-event-filters__group-title">Categoría</h4>
      <div class="v2-event-filters__chips" role="group" aria-label="Filtrar por categoría">
        <button
          v-for="cat in categoryOptions"
          :key="cat"
          type="button"
          class="v2-event-filters__chip"
          :class="{ 'v2-event-filters__chip--active': categoriesProxy.includes(cat) }"
          :aria-pressed="categoriesProxy.includes(cat)"
          @click="toggleCategory(cat)"
        >
          <span>{{ cat }}</span>
        </button>
      </div>
    </div>

    <div class="v2-event-filters__group">
      <h4 class="v2-event-filters__group-title">Fecha</h4>
      <div class="v2-event-filters__date">
        <BaseInput
          v-model="fromProxy"
          type="date"
          label="Desde"
          @input="onDateInput"
        />
        <BaseInput
          v-model="toProxy"
          type="date"
          label="Hasta"
          @input="onDateInput"
        />
      </div>
    </div>

    <button
      type="button"
      class="v2-event-filters__close"
      @click="$emit('close')"
      aria-label="Cerrar filtros"
    >
      <v-icon size="20">mdi-close</v-icon>
    </button>
  </aside>
</template>

<script>
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import { EVENT_TYPE_OPTIONS } from "./eventsHelpers";

export default {
  name: "EventFilters",
  components: { BaseInput },
  props: {
    search: { type: String, default: "" },
    types: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    from: { type: String, default: "" },
    to: { type: String, default: "" },
    categoryOptions: { type: Array, default: () => [] },
    mobileOpen: { type: Boolean, default: false },
  },
  emits: ["update:search", "update:types", "update:categories", "update:from", "update:to", "clear", "close"],
  data() {
    return {
      typeOptions: EVENT_TYPE_OPTIONS,
    };
  },
  computed: {
    searchProxy: {
      get() { return this.search; },
      set(v) { this.$emit("update:search", v); },
    },
    typesProxy: {
      get() { return this.types; },
      set(v) { this.$emit("update:types", v); },
    },
    categoriesProxy: {
      get() { return this.categories; },
      set(v) { this.$emit("update:categories", v); },
    },
    fromProxy: {
      get() { return this.from; },
      set(v) { this.$emit("update:from", v); },
    },
    toProxy: {
      get() { return this.to; },
      set(v) { this.$emit("update:to", v); },
    },
    hasActiveFilters() {
      return Boolean(this.search)
        || this.types.length > 0
        || this.categories.length > 0
        || Boolean(this.from)
        || Boolean(this.to);
    },
  },
  methods: {
    toggleType(value) {
      const list = [...this.types];
      const idx = list.indexOf(value);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(value);
      this.typesProxy = list;
    },
    toggleCategory(value) {
      const list = [...this.categories];
      const idx = list.indexOf(value);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(value);
      this.categoriesProxy = list;
    },
    onSearchInput() {},
    onDateInput() {},
    onClear() {
      this.$emit("clear");
    },
  },
};
</script>

<style scoped>
.v2-event-filters {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.v2-event-filters__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--color-border);
}

.v2-event-filters__title {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-headline);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  margin: 0;
}

.v2-event-filters__clear {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-primary);
  background: none;
  border: 0;
  padding: var(--space-1) var(--space-2);
  cursor: pointer;
  border-radius: var(--radius-md);
  transition: background-color var(--transition-fast);
}

.v2-event-filters__clear:hover {
  background-color: var(--color-primary-soft);
}

.v2-event-filters__group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.v2-event-filters__group-title {
  font-family: var(--font-body);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  color: var(--color-text-muted);
  margin: 0;
}

.v2-event-filters__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.v2-event-filters__chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-3);
  background-color: var(--color-surface-low);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  cursor: pointer;
  transition: background-color var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
}

.v2-event-filters__chip:hover {
  background-color: var(--color-primary-soft);
  border-color: var(--color-primary);
}

.v2-event-filters__chip--active {
  background-color: var(--color-primary);
  color: var(--color-primary-on);
  border-color: var(--color-primary);
}

.v2-event-filters__date {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2);
}

.v2-event-filters__close {
  display: none;
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
}

@media (max-width: 1023px) {
  .v2-event-filters {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--z-modal, 1000);
    overflow-y: auto;
    border-radius: 0;
    box-shadow: none;
    transform: translateX(-100%);
    transition: transform var(--transition-base);
  }

  .v2-event-filters--open {
    transform: translateX(0);
  }

  .v2-event-filters__close {
    display: inline-flex;
  }
}
</style>
